import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Field from 'components/forms/Field'
import routes from 'config/routes'
import { CurrencyContext } from 'contexts'
import { ErrorMessage, Field as FormikField, Form, Formik } from 'formik'
import moment from 'moment-timezone'
import PropTypes from 'prop-types'
import React from 'react'
import {
  Button,
  Card,
  Col,
  Form as BsForm,
  FormControl,
  InputGroup,
  Row,
} from 'react-bootstrap'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { parseError } from 'utils'
import * as Yup from 'yup'
import LoadingIcon from './LoadingIcon'

function SessionForm(props) {
  const currency = React.useContext(CurrencyContext)
  const initial = {
    earnings: '',
    ev: props.showEvField ? '' : 0,
    location: '',
    date: moment().format('yyyy-MM-DD'),
    hours: '',
    minutes: '',
    comments: '',
    player: props.username,
    ...props.initial,
  }

  // add custom values to initial
  props.customFields.forEach(
    custom =>
      (initial[`custom_${custom.id}`] = props.initial?.custom
        ? props.initial?.custom[custom.id] || ''
        : ''),
  )

  const validate = values => {
    const errors = {}
    // Truncate earnings to two decimal places
    if (values.earnings) {
      values.earnings = parseFloat(values.earnings).toFixed(2)
    }
    // Truncate ev to two decimal places
    if (values.ev) {
      values.ev = parseFloat(values.ev).toFixed(2)
    }
    return errors
  }

  const SessionSchema = Yup.object().shape({
    earnings: Yup.number().required('Required'),
    ev: Yup.number().required('Required'),
    location: Yup.string().required('Required'),
    player: Yup.string(),
    date: Yup.string()
      .required('Required')
      .matches(/^\d{4}-\d{2}-\d{2}$/, {
        message: 'Please enter a date as yyyy-mm-dd',
      }),
    hours: Yup.number().required('Hours Required'),
    minutes: Yup.number().required('Minutes Required'),
    comments: Yup.string(),
  })

  const submit = async (values, actions) => {
    const { hours, minutes, ...rest } = values
    const custom = {}
    Object.keys(rest).forEach(key => {
      if (key.startsWith('custom_')) {
        custom[key.slice(7)] = rest[key]
        delete rest[key]
      }
    })
    const prepared = {
      ...rest,
      custom,
      duration: `${hours}:${minutes}:00`,
    }
    try {
      await props.onSubmit(prepared)
    } catch (err) {
      const { field_errors } = parseError(err)
      actions.setErrors(field_errors)
      console.error('Error submitting session')
      console.error(err)
    }
  }

  return (
    <Card className="shadow">
      <Card.Body>
        <Card.Title>{props.editing ? 'EDIT' : 'ADD'} SESSION</Card.Title>
        <Formik
          enableReinitialize
          initialValues={initial}
          onSubmit={submit}
          validate={validate}
          validationSchema={SessionSchema}
        >
          {({ values, isSubmitting }) => (
            <Form>
              <Row>
                <Col>
                  <Field
                    name="location"
                    hint={
                      <a
                        href={`https://members.blackjackapprenticeship.com?cat=170&s=${encodeURIComponent(
                          values.location,
                        )}&post_type=casino`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Don&apos;t forget to update Casino411
                      </a>
                    }
                  />
                  {props.showPlayerField && <Field name="player" />}
                  <BsForm.Row>
                    <Col md={props.showEvField ? 6 : 12}>
                      <Field
                        name="earnings"
                        type="number"
                        step="any"
                        prepend={
                          <FontAwesomeIcon icon={currency.icon} fixedWidth />
                        }
                      />
                    </Col>
                    {props.showEvField && (
                      <Col md={6}>
                        <Field
                          name="ev"
                          label="HOURLY EV"
                          type="number"
                          step="any"
                          prepend={
                            <FontAwesomeIcon icon={currency.icon} fixedWidth />
                          }
                        />
                      </Col>
                    )}
                  </BsForm.Row>
                  <BsForm.Row>
                    <Col md={6}>
                      <Field name="date" type="date" placeholder="yyyy-mm-dd" />
                    </Col>
                    <Col md={6}>
                      <BsForm.Group>
                        <BsForm.Label className="font-weight-bold">
                          DURATION
                        </BsForm.Label>
                        <InputGroup>
                          <InputGroup.Prepend>
                            <InputGroup.Text>Hrs.</InputGroup.Text>
                          </InputGroup.Prepend>
                          <FormikField
                            name="hours"
                            type="number"
                            min="0"
                            as={FormControl}
                          />
                          <InputGroup.Prepend>
                            <InputGroup.Text className="border-left-0">
                              Mins.
                            </InputGroup.Text>
                          </InputGroup.Prepend>
                          <FormikField
                            name="minutes"
                            type="number"
                            min="0"
                            as={FormControl}
                          />
                        </InputGroup>
                        <ErrorMessage
                          name="hours"
                          component={BsForm.Text}
                          className="text-danger"
                        />
                        <ErrorMessage
                          name="minutes"
                          component={BsForm.Text}
                          className="text-danger"
                        />
                      </BsForm.Group>
                    </Col>
                  </BsForm.Row>
                </Col>
                <Col lg={5}>
                  <BsForm.Group>
                    <BsForm.Label className="font-weight-bold">
                      COMMENTS
                    </BsForm.Label>
                    <FormikField
                      name="comments"
                      as="textarea"
                      rows={5}
                      className="h-100 form-control"
                    />
                    <ErrorMessage
                      name="comments"
                      component={BsForm.Text}
                      className="text-danger"
                    />
                  </BsForm.Group>
                </Col>
              </Row>
              <Row className="my-3">
                <Col md={12} lg={7}>
                  <div className="custom-fields-title h6">Custom Fields</div>
                  {props.customFields.length > 0 ? (
                    <>
                      {props.customFields.map(custom => (
                        <Field
                          key={custom.id}
                          name={`custom_${custom.id}`}
                          label={custom.title}
                        />
                      ))}
                      <p className="small">
                        <Link to={routes.settings}>Manage custom fields</Link>
                      </p>
                    </>
                  ) : (
                    <p className="small">
                      <Link to={routes.settings}>Add custom fields</Link> to
                      track any additional information about your sessions that
                      you think is useful.
                    </p>
                  )}
                </Col>
              </Row>
              <Button variant="warning" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <LoadingIcon size="xs" /> : 'SAVE'}
              </Button>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  )
}

SessionForm.propTypes = {
  customFields: PropTypes.array,
  editing: PropTypes.bool,
  initial: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  showEvField: PropTypes.bool,
  showPlayerField: PropTypes.bool,
}

SessionForm.defaultProps = {
  customFields: [],
  editing: false,
  initial: {},
  showEvField: true,
  showPlayerField: false,
}

const mapState = state => ({
  username: state.auth.username,
})

export default connect(mapState)(SessionForm)
